import React, { createContext, useContext, useEffect, useRef, useState } from "react";

// --- Context Setup ---
const TaskStatusContext = createContext();

export const TaskStatusProvider = ({ children }) => {
  const [taskStatuses, setTaskStatuses] = useState({});
  const [taskData, setTaskData] = useState({});
  const [taskConnections, setTaskConnections] = useState({});
  const wsRefs = useRef({}); // TaskID -> WebSocket instances

  // --- Create WebSocket and track connection status ---
  const createWebSocketWithStatus = (url, taskId, onMessage, maxRetries = 3, retryDelay = 2000) => {
    console.log("createWebSocketWithStatus",url)
    let retries = 0;
    let ws;

    const connect = () => {
      ws = new WebSocket(url);

      ws.onopen = () => {
        console.log(`taskws open WebSocket for ${taskId} connected`);
        setTaskConnections((prev) => ({ ...prev, [taskId]: true }));
        retries = 0; // reset retry count on successful connect
      };

      ws.onmessage = (event) => {
        try {
          console.log("taskws onmessage")
          const data = JSON.parse(event.data);
          onMessage(data);
        } catch (e) {
          console.warn("Invalid WS data", e);
        }
      };

      ws.onerror = (err) => {
        console.log("taskws on error")
        console.error(`WebSocket error on ${taskId}:`, err);
                // Retry if needed
        if (retries < maxRetries) {
          retries++;
          console.log(`Reconnecting ${taskId} in ${retryDelay}ms... (attempt ${retries})`);
          setTimeout(connect, retryDelay);
        } else {
          console.error(`WebSocket for ${taskId} failed to reconnect after ${maxRetries} attempts`);
        }
      };

      ws.onclose = (event) => {
        console.log("taskws onclose")
        console.warn(`WebSocket for ${taskId} closed:`, event.reason || 'Unknown reason');
        setTaskConnections((prev) => ({ ...prev, [taskId]: false }));
      };
    };
    if (url) {
       connect(); // initial connect
    }

   
    return ws;
  };


  // --- Register a new task and listen via WebSocket ---
  const registerTask = (taskId, wsUrl) => {
    console.log("registerTask", taskId);
    localStorage.setItem(taskId, wsUrl);

    if (wsRefs.current[taskId]) {
      const ws = wsRefs.current[taskId];
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
      delete wsRefs.current[taskId];
    }

    const ws = createWebSocketWithStatus(wsUrl, taskId, (message) => {
      // Update both status and full task data
      setTaskStatuses((prev) => ({
        ...prev,
        [taskId]: message.status || message,
      }));

      setTaskData((prev) => ({
        ...prev,
        [taskId]: message,
      }));
    });

    wsRefs.current[taskId] = ws;
  };

  // --- Deregister a task and stop listening ---
  const deregisterTask = (taskId) => {
    localStorage.removeItem(taskId)
    if (wsRefs.current[taskId]) {
      wsRefs.current[taskId].close();
      delete wsRefs.current[taskId];
    }

    setTaskStatuses((prev) => {
      const updated = { ...prev };
      delete updated[taskId];
      return updated;
    });

    setTaskData((prev) => {
      const updated = { ...prev };
      delete updated[taskId];
      return updated;
    });

    setTaskConnections((prev) => {
      const updated = { ...prev };
      delete updated[taskId];
      return updated;
    });
  };

  // --- Getters ---
  const getTaskStatus = (taskId) => taskStatuses[taskId];
  const getTaskData = (taskId) => taskData[taskId];
  const getConnectionStatus = (taskId) => taskConnections[taskId] ?? false;

  // --- Cleanup all WebSockets on unmount ---
  useEffect(() => {
    return () => {
      Object.values(wsRefs.current).forEach((ws) => ws.close());
      wsRefs.current = {};
    };
  }, []);

  return (
    <TaskStatusContext.Provider
      value={{
        registerTask,
        deregisterTask,
        getTaskStatus,
        getTaskData,
        getConnectionStatus,
        taskStatuses,
        taskData,
        taskConnections,
      }}
    >
      {children}
    </TaskStatusContext.Provider>
  );
};

// --- Hook to use the task status context ---
export const useTaskStatus = () => useContext(TaskStatusContext);
